import { forwardRef, useState } from "react";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { useModal } from "../../context/ModalContext";
import PaymentModal from "../../components/PaymentMethods/PaymentModal";
import {
  calculateDGCoinAmount,
  convertCurrencyAmount,
  formatCurrency,
  getRequestedAmount,
} from "../../utils/utils";
import { trackEvent } from "../../lib/appsflyer";
import { ShoppingBagIcon } from "../../assets/icons";
import {
  addToCart,
  incrementQuantity,
  decrementQuantity,
  generateCartItemId,
  selectUserCartItems,
  removeFromCart,
} from "../../features/store/cartSlice";
import { computeCartItemData, getActiveUserId } from "../Cart/utils";
import { HiCheck, HiTrash } from "react-icons/hi";

type TransactionDataProps = {
  type: string;
  plan_id?: string;
  unique_number?: string;
  quantity?: string;
  additionalInputValue?: string;
  recipientLabel?: string;
  additionalInputLabel?: string;
  requiresRecipient?: boolean;
  requiresAdditionalInput?: boolean;
  onValidationError?: (error: {
    field: "unique_number" | "additional" | "none";
    message: string | null;
  }) => void;
};

const PaymentCard = forwardRef<
  HTMLButtonElement,
  TransactionDataProps & { selectedPlan?: any }
>((props, ref) => {
  const { darkMode } = useDarkMode();
  const [localQuantity, setLocalQuantity] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { data: appData } = useAppSelector((state) => state.appData);
  const { selectedCurrency } = useAppSelector((state) => state.locale);
  const { user } = useAppSelector((state) => state.auth);
  const { openLoginModal } = useModal();
  const dispatch = useAppDispatch();

  const userId = getActiveUserId(user);
  const cartItems = useAppSelector((state) =>
    selectUserCartItems(state, userId),
  );

  // Normalize string properties to guarantee matching cartItemId formats
  const cleanUniqueNumber = props.unique_number?.trim().toLowerCase() || "";
  const cleanAdditionalInput =
    props.additionalInputValue?.trim().toLowerCase() || "";

  const targetItemId = props.selectedPlan
    ? generateCartItemId({
        categoryId:
          props.selectedPlan.category_id || props.selectedPlan.category?.id,
        planId: props.selectedPlan.id,
        type: props.type,
        unique_number: cleanUniqueNumber,
        additionalInputValue: cleanAdditionalInput,
      })
    : "";

  const existingCartItem = cartItems.find((item) => {
    if (item.id === targetItemId) return true;

    if (!props.selectedPlan) return false;

    const planId = String(props.selectedPlan.id ?? "");
    const planCategoryId = String(
      props.selectedPlan.category_id ?? props.selectedPlan.category?.id ?? "",
    );

    const itemPlanId = String(item.planId ?? "");
    const itemCategoryId = String(item.categoryId ?? "");

    // Normalize user inputs on the cart item
    const itemUniqueNumber = (item.unique_number ?? "").trim().toLowerCase();

    const itemAdditionalInput = (item.additionalInputValue ?? "")
      .trim()
      .toLowerCase();

    return (
      itemPlanId === planId &&
      itemCategoryId === planCategoryId &&
      item.type === props.type &&
      itemUniqueNumber === cleanUniqueNumber &&
      itemAdditionalInput === cleanAdditionalInput
    );
  });

  const isInCart = Boolean(existingCartItem);
  const matchedItemId = existingCartItem?.id || targetItemId;
  const activeQuantity = existingCartItem
    ? existingCartItem.quantity
    : localQuantity;

  const currencies = appData?.currencies || [];
  const getCurrencyById = (currencyId?: number) =>
    currencies.find((currency: any) => currency.id === currencyId);
  const dgCoinCurrency =
    currencies.find(
      (currency: any) => currency.code?.toLowerCase() === "dgc",
    ) ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg coin"),
    );

  const formatConvertedPrice = (
    amount: number | string,
    currencyId?: number,
  ) => {
    const fromCurrency =
      getCurrencyById(currencyId) || selectedCurrency || currencies[0];
    const toCurrency = selectedCurrency || fromCurrency;
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      toCurrency,
    );
    return {
      numeric: converted,
      symbol: toCurrency?.symbol || "",
      value: formatCurrency(converted),
    };
  };

  const formatDGCoinPrice = (amount: number | string, currencyId?: number) => {
    const rawCoins = calculateDGCoinAmount(
      amount,
      currencyId,
      currencies,
      dgCoinCurrency,
    );
    return formatCurrency(rawCoins);
  };

  const togglePaymentModal = () => {
    setShowPaymentModal(!showPaymentModal);
  };

  const validateInputs = () => {
    if (props.requiresRecipient && !props.unique_number?.trim()) {
      props.onValidationError?.({
        field: "unique_number",
        message: `${props.recipientLabel || "Recipient"} is required.`,
      });
      return false;
    }

    if (props.requiresAdditionalInput && !props.additionalInputValue?.trim()) {
      props.onValidationError?.({
        field: "additional",
        message: `${props.additionalInputLabel || "Details"} is required.`,
      });
      return false;
    }
    props.onValidationError?.({ field: "none", message: null });
    return true;
  };

  const handleIncrement = () => {
    if (!user) {
      openLoginModal();
      return;
    }

    if (!validateInputs()) return;

    if (isInCart) {
      dispatch(incrementQuantity({ userId, itemId: matchedItemId }));
    } else {
      setLocalQuantity((q) => q + 1);
    }
  };

  const handleDecrement = () => {
    if (!user) {
      openLoginModal();
      return;
    }

    if (isInCart) {
      dispatch(decrementQuantity({ userId, itemId: matchedItemId }));
    } else {
      setLocalQuantity((q) => Math.max(1, q - 1));
    }
  };

  const handleAddToCart = () => {
    if (!user) {
      openLoginModal();
      return;
    }

    if (!validateInputs()) return;

    if (isInCart) {
      dispatch(incrementQuantity({ userId, itemId: matchedItemId }));
      return;
    }

    const plan = {
      ...props.selectedPlan,
      plan_discount: props.selectedPlan.discount,
    };

    const {
      discountPercent,
      isFixed,
      rawBonusAmount,
      rawOriginalPrice,
      rawUnitDiscount,
      rawWalletPrice,
    } = computeCartItemData(
      plan,
      false,
      props.type,
      props.additionalInputValue,
    );

    dispatch(
      addToCart({
        userId,
        item: {
          planId: plan.id,
          categoryId: plan.category_id || plan.category?.id,
          type: props.type,
          name: plan.name,
          product: plan.category?.name || props.type,
          image:
            plan.image_url ||
            plan.category?.provider?.image_url ||
            plan.provider?.image_url ||
            "",
          atm_price: Number(plan.atm_price ?? 0),
          wallet_price: Number(plan.wallet_price ?? 0),
          price: rawWalletPrice,
          originalPrice: rawOriginalPrice,
          discount: rawUnitDiscount,
          plan_discount: plan.discount,
          discountPercentage: discountPercent,
          bonusPoints: Math.round(rawBonusAmount),
          isFixed,
          quantity: localQuantity,
          unique_number: cleanUniqueNumber,
          additionalInputValue: cleanAdditionalInput,
          priceCurrencyId: plan.price_currency_id,
          price_type: plan?.price_type,
          point_bonus: plan?.point_bonus,
          bonus_points: plan?.bonus_points,
          points: plan?.points,
        },
      }),
    );
  };

  const handlePayNowClick = () => {
    if (!user) {
      openLoginModal();
      return;
    }

    if (!validateInputs()) return;

    trackEvent("af_initiated_checkout", {
      af_content_type: props.type,
      af_content_id: props.plan_id,
      af_quantity: activeQuantity,
    });
    togglePaymentModal();
  };

  const removeCartItem = () => {
    dispatch(removeFromCart({ userId, itemId: matchedItemId }));
  };

  return (
    <>
      {props.selectedPlan &&
        (() => {
          const hasDiscount =
            props.selectedPlan.discount > 0 &&
            props.selectedPlan.discount > props.selectedPlan.wallet_price;
          const discountPercent = hasDiscount
            ? ((props.selectedPlan.discount - props.selectedPlan.wallet_price) /
                props.selectedPlan.discount) *
              100
            : 0;
          const { symbol, value } = formatConvertedPrice(
            props.selectedPlan.wallet_price,
            props.selectedPlan.price_currency_id,
          );
          const dgCoinValue = formatDGCoinPrice(
            props.selectedPlan.wallet_price,
            props.selectedPlan.price_currency_id,
          );
          const convertedOriginal = hasDiscount
            ? formatConvertedPrice(
                props.selectedPlan.discount,
                props.selectedPlan.price_currency_id,
              )
            : null;

          return (
            <div>
              <div className="flex flex-row items-center gap-3">
                <img
                  src={
                    props.selectedPlan.image_url ||
                    props.selectedPlan.category?.provider?.image_url ||
                    props.selectedPlan.provider?.image_url ||
                    ""
                  }
                  alt="CP"
                  className="w-8 bp:w-20 aspect-square rounded-md object-cover flex-shrink-0"
                />
                <div className="flex flex-col w-full">
                  <div className="w-full bp:w-[350px] lg:w-[500px] dark:bg-[#FFFFFF1A] border border-border-border1 dark:border-tabs p-4 rounded-lg flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] bp:text-base font-semibold font-lt text-black dark:text-white">
                        {props.selectedPlan.name}
                      </p>
                      <p className="text-[8px] bp:text-sm text-gray-500 font-lt dark:text-gray-400">
                        Product: {props.selectedPlan.category?.name}
                      </p>
                      {props.selectedPlan.price_type === "fixed" ? (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <div className="bg-[#039EF2] text-white px-1 bp:px-1.5 bp:py-0.5 rounded-2xl flex items-center gap-1 shrink-0">
                            <div className="w-3 bp:w-4 h-3 bp:h-4 flex items-center justify-center">
                              <img
                                src="/png/dg.png"
                                alt="Coins"
                                className="w-[14px] h-[14px]"
                              />
                            </div>
                            <p className="text-[8px] bp:text-sm font-semibold whitespace-nowrap">
                              {dgCoinValue}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] bp:text-base text-black dark:text-white whitespace-nowrap">
                              {symbol} {value}
                            </span>
                            {convertedOriginal && (
                              <span className="text-gray-500 bp:text-xs text-[10px] line-through whitespace-nowrap">
                                {convertedOriginal.symbol}
                                {convertedOriginal.value}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[8px] bp:text-base text-black dark:text-white whitespace-nowrap">
                            {props.selectedPlan.wallet_price}%
                          </span>
                          {hasDiscount && (
                            <span className="text-gray-500 text-[8px] bp:text-xs line-through whitespace-nowrap">
                              {props.selectedPlan.discount}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {hasDiscount && (
                      <span className="bg-[#FEF3F2] rounded-xl shrink-0">
                        <p className="text-[#D92D20] text-[9px] font-semibold font-satoshi px-[2px] py-[1px] whitespace-nowrap">
                          {Math.round(discountPercent)}% off
                        </p>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-row gap-3 mt-3">
                {/* spacer the width of the product image, to line up with the card */}
                <div className="w-8 bp:w-20 flex-shrink-0" />
                <div className="flex items-center justify-between w-full bp:w-[350px] lg:w-[500px]">
                  <span className="text-[10px] bp:text-sm font-medium text-gray-600 dark:text-gray-300">
                    Quantity
                  </span>
                  <div
                    className={`flex items-center border rounded-lg p-1 transition-all duration-200 ${
                      isInCart
                        ? "border-green-500/50 bg-green-50/50 dark:bg-green-950/20 dark:border-green-500/30"
                        : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={handleDecrement}
                      aria-label={
                        isInCart && activeQuantity === 1
                          ? "Remove from cart"
                          : "Decrease quantity"
                      }
                      className="px-2 py-0.5 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      {isInCart && activeQuantity === 1 ? (
                        <HiTrash className="w-3.5 h-3.5 text-red-500" />
                      ) : (
                        "-"
                      )}
                    </button>

                    <span className="px-3 text-xs font-semibold text-black dark:text-white">
                      {activeQuantity}
                    </span>

                    <button
                      type="button"
                      onClick={handleIncrement}
                      aria-label="Increase quantity"
                      className="px-2 py-0.5 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end items-center gap-3 w-full mt-4">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-[10px] bp:text-sm font-semibold shadow-sm transition-colors ${
                    isInCart
                      ? "bg-green-600 hover:bg-green-700 border-green-600 text-white"
                      : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {isInCart ? (
                    <>
                      <HiCheck className="w-3.5 h-3.5 text-white" />
                      <span>In Cart ({activeQuantity})</span>
                    </>
                  ) : (
                    <>
                      <ShoppingBagIcon theme={darkMode ? "dark" : "light"} />
                      <span>Add to Cart</span>
                    </>
                  )}
                </button>

                <button
                  ref={ref}
                  type="button"
                  onClick={handlePayNowClick}
                  className="px-4 py-2 text-[10px] bp:text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2]"
                >
                  Pay Now
                </button>
              </div>

              <PaymentModal
                isOpen={showPaymentModal}
                togglePaymentModal={togglePaymentModal}
                selectedPlan={props.selectedPlan}
                unique_number={props.unique_number}
                type={props.type}
                additionalInputValue={props.additionalInputValue}
                isInCart={isInCart}
                quantity={activeQuantity}
                userId={userId}
                removeFromCart={removeCartItem}
              />
            </div>
          );
        })()}
    </>
  );
});

export default PaymentCard;
